import type { ReactNode } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { getBoardCardTagTooltip } from "./boardCardTagTooltip";
import styles from "./BoardCardAvatarField.module.scss";

interface BoardCardAvatarFieldProps {
    /** The field, e.g. "Created by": read first, and first in the tooltip. */
    field: string;
    /**
     * Who the avatar shows, e.g. "Matt Peterson". With it the field gets one
     * `Field · Value` tooltip; without it each avatar in `children` brings
     * its own, e.g. a Visibility stack's "Visibility · Acme Co".
     */
    value?: string;
    /** A 20px avatar or avatar group, the height of the tags beside it. */
    children: ReactNode;
}

/**
 * A board card field drawn as avatars on their own, with no tag around
 * them, in line with the 20px tags. Screen readers hear the field name
 * first. Hovering shows the field's tooltip; clicks fall through to the card.
 */
export const BoardCardAvatarField = ({
    field,
    value,
    children,
}: Readonly<BoardCardAvatarFieldProps>) => {
    const spokenPrefix = value === undefined ? field : `${field}, ${value}`;
    const content = (
        <span className={styles.root}>
            <span className={styles.hidden}>{spokenPrefix} </span>
            <span
                className={styles.avatars}
                aria-hidden={value !== undefined || undefined}
            >
                {children}
            </span>
        </span>
    );
    if (value === undefined) {
        return content;
    }
    return (
        <Tooltip
            content={getBoardCardTagTooltip({
                field,
                value,
                tooltip: "fieldAndValue",
                isTruncated: false,
            })}
        >
            {content}
        </Tooltip>
    );
};
